import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, BarChart3 } from "lucide-react";
import { adaptMatchStats } from "../../utils/matchStatsAdapters";

// "Match Stats Type 1" — used for Hold'em (wild-west-poker), Blackjack, Video
// Poker and Darts. Layout (top → bottom): back-only header, a YOU-vs-OPPONENT
// score-progression chart, simplified score cards, then per-round profit/loss
// bars. Color Sort reuses this layout without the per-round bars (its adapter
// returns empty `rounds`, so the bars section is omitted — chart-only).
// The chart and the bars sweep in left→right on open (annotation).
// There is deliberately NO close/X button — the back arrow returns to the Game
// Results popup rendered underneath.

// Win/loss status colours, mirrored from MatchResultSummaryPopUp so the "You"
// pill and the user's chart line/bar share the same green(win)/red(loss) hue.
const STATUS_HEX = {
  win: "#53D337",
  loss: "#F84E4E",
  tie: "#4E89F8",
};
const YOU_GRADIENT = {
  win: "linear-gradient(180deg, #53D337 0%, #368C24 100%)",
  loss: "linear-gradient(180deg, #F84E4E 0%, #9B2B2B 100%)",
  tie: "linear-gradient(180deg, #4E89F8 0%, #3B67B9 100%)",
};
const OPPONENT_HEX = "#B5B5B5";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Bar value text. `signed` games (poker/darts) prefix a `+` on gains; games
// whose bars are plain counts/totals (e.g. Shark Pool) pass signed=false.
const formatBarValue = (v, signed) => {
  const n = Number(Number(v).toFixed(2));
  return signed && n > 0 ? `+${n}` : `${n}`;
};

// Maps a numeric series to [x, y] pixel coords within the viewBox. `padY`
// insets the plot vertically so per-point labels have headroom top/bottom.
const toCoords = (series, width, height, min, max, padY = 0) => {
  if (!series.length) return [];
  const span = max - min || 1;
  const stepX = series.length > 1 ? width / (series.length - 1) : 0;
  const usableH = height - padY * 2;
  return series.map((v, i) => {
    const x = series.length > 1 ? i * stepX : width / 2;
    const y = padY + (1 - (v - min) / span) * usableH;
    return [x, y];
  });
};

const toPointsAttr = (coords) =>
  coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

// Points whose value differs from the previous one — so a flat run of equal
// totals is labeled once, at its start. The first point is always included.
const changePoints = (coords, series) =>
  coords
    .map(([x, y], i) => ({ x, y, v: series[i] }))
    .filter((_, i) => i === 0 || series[i] !== series[i - 1]);

// Cap on how many value labels a single line shows. Long games run 25-35 
// scoring events, which crowds the chart, so beyond the cap we keep only
// every Nth point — always the first and last (final score). An adapter can
// lower the cap for its game via `maxChartLabels`.
const MAX_CHART_LABELS = 10;
const thinLabels = (points, maxLabels = MAX_CHART_LABELS) => {
  if (points.length <= maxLabels) return points;
  const stride = Math.ceil(points.length / maxLabels);
  const last = points.length - 1;
  return points.filter((_, i) => i % stride === 0 || i === last);
};

// `showLabels` prints the cumulative total next to each index — on by adapter
// opt-in (`chartLabels`) for the flat event-log games (Color Sort, Shark Pool)
// whose per-index totals are the point; other games render just the lines with
// end dots.
const ScoreProgressionChart = ({
  userSeries,
  opponentSeries,
  userColor,
  showLabels = false,
  maxLabels,
}) => {
  const W = 300;
  const H = 130;
  // Leave headroom for the value labels so they aren't clipped by the reveal
  // mask; the plain line charts (no labels) use the full height.
  const padY = showLabels ? 20 : 0;
  const all = [...userSeries, ...opponentSeries];
  if (all.length === 0) return null;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const userCoords = toCoords(userSeries, W, H, min, max, padY);
  const oppCoords = toCoords(opponentSeries, W, H, min, max, padY);
  const userPts = toPointsAttr(userCoords);
  const oppPts = toPointsAttr(oppCoords);
  const userEnd = userCoords[userCoords.length - 1] ?? null;
  const oppEnd = oppCoords[oppCoords.length - 1] ?? null;
  const userLabels = showLabels ? thinLabels(changePoints(userCoords, userSeries), maxLabels) : [];
  const oppLabels = showLabels ? thinLabels(changePoints(oppCoords, opponentSeries), maxLabels) : [];

  return (
    <div className="relative h-full w-full">
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-full"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {[0.25, 0.5, 0.75].map((f) => (
        <line
          key={f}
          x1={W * f}
          y1="0"
          x2={W * f}
          y2={H}
          stroke="#ffffff14"
          strokeWidth="1"
          strokeDasharray="3 4"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {oppPts && (
        <polyline
          points={oppPts}
          fill="none"
          stroke={OPPONENT_HEX}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {userPts && (
        <polyline
          points={userPts}
          fill="none"
          stroke={userColor}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {oppEnd && <circle cx={oppEnd[0]} cy={oppEnd[1]} r="4" fill={OPPONENT_HEX} />}
      {userEnd && <circle cx={userEnd[0]} cy={userEnd[1]} r="4" fill={userColor} />}
    </svg>
      {renderLabels(userLabels, W, H, userColor, "above", "u")}
      {renderLabels(oppLabels, W, H, OPPONENT_HEX, "below", "o")}
    </div>
  );
};

// Overlays the value labels as crisp HTML (SVG text would distort under the
// chart's non-uniform scaling). Edge points anchor to the edge so the first /
// final totals aren't clipped; `place` puts a line's labels above or below its
// points so the two series' numbers don't collide.
function renderLabels(labels, W, H, color, place, keyPrefix) {
  const vy = place === "above" ? "calc(-100% - 3px)" : "3px";
  return labels.map(({ x, y, v }, i) => {
    const vx = x >= W - 0.5 ? "-100%" : x <= 0.5 ? "0%" : "-50%";
    return (
      <span
        key={`${keyPrefix}l${i}`}
        className="absolute text-[9px] font-bold leading-none whitespace-nowrap pointer-events-none"
        style={{
          left: `${(x / W) * 100}%`,
          top: `${(y / H) * 100}%`,
          color,
          transform: `translate(${vx}, ${vy})`,
        }}
      >
        {v}
      </span>
    );
  });
}

const BreakdownBar = ({ youValue, oppValue, statusHex, revealed, animate }) => {
  const youAbs = Math.abs(youValue);
  const oppAbs = Math.abs(oppValue);
  const total = youAbs + oppAbs;
  const youPct = total === 0 ? 50 : (youAbs / total) * 100;

  return (
    <div className="h-2 w-full flex rounded-full overflow-hidden bg-[#2A2A2A]">
      <div
        className="h-full"
        style={{
          width: revealed ? `${youPct}%` : "0%",
          backgroundColor: statusHex,
          transition: animate ? "width 700ms ease-out" : "none",
        }}
      />
      <div className="h-full flex-1 bg-[#5A5A5A]" />
    </div>
  );
};

const MatchStatsModal = ({
  isOpen = true,
  onBack,
  gameName,
  breakdown,
  opponentBreakdown,
  userName = "You",
  opponentName,
  userScore,
  opponentScore,
  status,
}) => {
  const [revealed, setRevealed] = useState(false);
  const animate = !prefersReducedMotion();

  const you = useMemo(
    () => adaptMatchStats(gameName, breakdown),
    [gameName, breakdown],
  );
  const opp = useMemo(
    () => adaptMatchStats(gameName, opponentBreakdown),
    [gameName, opponentBreakdown],
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const id = requestAnimationFrame(() => setRevealed(true));
      return () => {
        cancelAnimationFrame(id);
        document.body.style.overflow = "unset";
      };
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen || !you) return null;

  const statusHex = STATUS_HEX[status] ?? STATUS_HEX.tie;
  const youGradient = YOU_GRADIENT[status] ?? YOU_GRADIENT.tie;
  const userSeries = you.series ?? [];
  const opponentSeries = opp?.series ?? [];
  const rowCount = Math.max(
    you.rounds.length,
    opp?.rounds.length ?? 0,
  );
  // Bars default to signed (+/-) profit/loss; adapters opt out for plain
  // count/total bars (Shark Pool) via `signedBars: false`.
  const signedBars = you.signedBars !== false;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Match Stats"
    >
      <div className="w-full max-w-sm bg-[rgb(15,15,15)] rounded-2xl shadow-xl relative animate-in zoom-in-95 duration-200 flex flex-col max-h-[92vh]">
        {/* Header — back only, no close */}
        <div className="relative flex items-center justify-center px-6 pt-5 pb-3 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:bg-black/80 transition-colors border border-white/10"
            aria-label="Back to results"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 font-saira font-extrabold tracking-wide text-white text-lg">
            <BarChart3 className="w-5 h-5" />
            MATCH STATS
          </div>
        </div>

        <div className="overflow-y-auto min-h-0 px-6 pb-6">
          {/* Score-progression chart */}
          <div className="relative rounded-xl bg-[#161616] border border-white/5 p-3 mb-5">
            <div
              className="h-36 w-full"
              style={{
                clipPath: revealed ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
                transition: animate ? "clip-path 900ms ease-out" : "none",
              }}
            >
              <ScoreProgressionChart
                userSeries={userSeries}
                opponentSeries={opponentSeries}
                userColor={statusHex}
                showLabels={you.chartLabels === true}
                maxLabels={you.maxChartLabels}
              />
            </div>
            <div className="text-center text-[10px] text-darkGray tracking-wide mt-1">
              MATCH TIMELINE
            </div>
          </div>

          {/* Score cards (simplified, no enlargement) */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center space-y-1">
              <div
                className={`inline-block font-bold text-xs px-4 py-1 ${status === "win" ? "text-black" : "text-white"}`}
                style={{ borderRadius: "35.947px", background: youGradient }}
              >
                You
              </div>
              <div className="text-[16px] truncate">{userName}</div>
              <div className="text-2xl font-bold font-saira">
                {userScore ?? "-"}
              </div>
            </div>
            <div className="text-center space-y-1">
              <div
                className="inline-block font-bold text-xs px-4 py-1 text-black"
                style={{
                  borderRadius: "35.947px",
                  background: "linear-gradient(180deg, #B5B5B5 0%, #6E6E6E 100%)",
                }}
              >
                Opponent
              </div>
              <div className="text-[16px] truncate">{opponentName ?? "-"}</div>
              <div className="text-2xl font-bold font-saira">
                {opponentScore ?? "-"}
              </div>
            </div>
          </div>

          {/* Per-round breakdown bars — omitted for chart-only games (e.g.
              Color Sort) whose adapter returns no rounds. */}
          {rowCount > 0 && (
          <div className="space-y-3">
            {Array.from({ length: rowCount }).map((_, i) => {
              const youRow = you.rounds[i];
              const oppRow = opp?.rounds?.[i];
              const label = youRow?.label ?? oppRow?.label ?? `#${i + 1}`;
              return (
                <div key={i}>
                  <div className="text-center text-[10px] font-bold text-darkGray tracking-wide mb-1">
                    {label}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold w-12 text-left shrink-0">
                      {youRow ? formatBarValue(youRow.value, signedBars) : ""}
                    </span>
                    <div className="flex-1">
                      <BreakdownBar
                        youValue={youRow?.value ?? 0}
                        oppValue={oppRow?.value ?? 0}
                        statusHex={statusHex}
                        revealed={revealed}
                        animate={animate}
                      />
                    </div>
                    <span className="text-sm font-bold w-12 text-right shrink-0">
                      {oppRow ? formatBarValue(oppRow.value, signedBars) : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchStatsModal;
